import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  completionKey,
  createHabit,
  deleteOccurrence,
  endSeriesFrom,
  getOccurrencesInRange,
  groupByDate,
  isRepeating,
  moveOccurrence,
  toggleCompletion,
} from '../utils/habits.js';
import {
  applySportValue,
  isSportComplete,
  normalizeSportConfig,
  pruneSportValues,
} from '../utils/sport.js';
import { loadState, saveState } from '../utils/storage.js';

/**
 * Single source of truth for habits + completions, persisted to localStorage.
 *
 * All mutations go through here so persistence can never be forgotten.
 */
export function useHabits() {
  // Lazy initialiser: read localStorage once, on mount.
  const [state, setState] = useState(() => loadState());
  // True once a write has failed — private browsing, a full quota, or storage
  // blocked outright. Silently losing someone's notebook is not acceptable, so
  // the UI gets told and can warn them.
  const [storageFailed, setStorageFailed] = useState(false);

  // Persist on every change. Cheap — the payload is tiny.
  useEffect(() => {
    setStorageFailed(!saveState(state));
  }, [state]);

  /** Add a habit on a given day. */
  const addHabit = useCallback((name, repeat, dateKey) => {
    if (!name || !name.trim()) return;
    setState((prev) => ({
      ...prev,
      habits: [...prev.habits, createHabit({ name, repeat, dateKey })],
    }));
  }, []);

  /**
   * Add a sport habit. The blueprint is normalised here so a half-filled modal
   * can never reach storage.
   * @param {{name: string, repeat: string, sport: object}} draft
   * @param {string} dateKey
   */
  const addSportHabit = useCallback((draft, dateKey) => {
    const sport = normalizeSportConfig(draft?.sport);
    const name = String(draft?.name || '').trim();
    if (!sport || !name) return;
    setState((prev) => ({
      ...prev,
      habits: [...prev.habits, createHabit({ name, repeat: draft.repeat, dateKey, sport })],
    }));
  }, []);

  /** Replace one habit via an updater function. */
  const updateHabit = useCallback((habitId, updater) => {
    setState((prev) => ({
      ...prev,
      habits: prev.habits.map((h) => (h.id === habitId ? updater(h) : h)),
    }));
  }, []);

  /** Drop a habit and everything recorded against it. */
  const removeHabit = useCallback((habitId) => {
    const mine = (key) => key.startsWith(`${habitId}|`);
    setState((prev) => ({
      habits: prev.habits.filter((h) => h.id !== habitId),
      // Also clear its completion + log entries so storage doesn't grow forever.
      completions: Object.fromEntries(
        Object.entries(prev.completions).filter(([key]) => !mine(key))
      ),
      sportLogs: Object.fromEntries(Object.entries(prev.sportLogs).filter(([key]) => !mine(key))),
    }));
  }, []);

  /**
   * Delete an occurrence.
   * scope 'day'    -> hide just this day (one-offs are removed outright)
   * scope 'series' -> stop the weekly habit from this day onward
   */
  const deleteHabitOccurrence = useCallback(
    (habit, baseDateKey, scope = 'day') => {
      if (!isRepeating(habit) || (scope === 'series' && baseDateKey <= habit.startDate)) {
        removeHabit(habit.id);
        return;
      }
      updateHabit(habit.id, (h) =>
        scope === 'series' ? endSeriesFrom(h, baseDateKey) : deleteOccurrence(h, baseDateKey)
      );
    },
    [removeHabit, updateHabit]
  );

  /** Drag-and-drop: move a single occurrence to another day. */
  const moveHabitOccurrence = useCallback(
    (habit, baseDateKey, targetDateKey) => {
      if (!targetDateKey || targetDateKey === baseDateKey) return;
      updateHabit(habit.id, (h) => moveOccurrence(h, baseDateKey, targetDateKey));
    },
    [updateHabit]
  );

  /** Tick / untick one occurrence. */
  const toggleDone = useCallback((habitId, baseDateKey) => {
    setState((prev) => ({
      ...prev,
      completions: toggleCompletion(prev.completions, habitId, baseDateKey),
    }));
  }, []);

  /**
   * Record one numeric field of a sport session.
   *
   * The tick is derived, never toggled by hand: a sport occurrence is done
   * exactly when every field of that session holds a number, so the completion
   * map is rewritten alongside the log in the same update.
   *
   * Writing the first set of a station also fills the station's other sets —
   * applySportValue owns that rule, so the log and the tick stay consistent.
   *
   * @param {object} habit the sport habit
   * @param {string} baseDateKey occurrence being logged
   * @param {string} fieldKey `stationId:setIndex:metricId`
   * @param {string} value raw text from the input
   */
  const setSportField = useCallback((habit, baseDateKey, fieldKey, value) => {
    const logKey = completionKey(habit.id, baseDateKey);
    setState((prev) => {
      const values = applySportValue(habit.sport, prev.sportLogs[logKey] || {}, fieldKey, value);
      const pruned = pruneSportValues(habit.sport, values);
      const completions = { ...prev.completions };

      if (isSportComplete(habit.sport, pruned)) completions[logKey] = true;
      else delete completions[logKey];

      return { ...prev, completions, sportLogs: { ...prev.sportLogs, [logKey]: pruned } };
    });
  }, []);

  return {
    habits: state.habits,
    completions: state.completions,
    sportLogs: state.sportLogs,
    storageFailed,
    addHabit,
    addSportHabit,
    updateHabit,
    removeHabit,
    deleteHabitOccurrence,
    moveHabitOccurrence,
    toggleDone,
    setSportField,
  };
}

/**
 * Occurrences across a date range, bucketed by day key.
 * Memoised so dragging doesn't recompute the whole schedule on every frame.
 * @param {object[]} habits
 * @param {string} startKey first day of the rendered range
 * @param {string} endKey last day of the rendered range
 */
export function useOccurrences(habits, startKey, endKey) {
  return useMemo(() => {
    const occurrences = getOccurrencesInRange(habits, startKey, endKey);
    return { occurrences, byDate: groupByDate(occurrences) };
  }, [habits, startKey, endKey]);
}
